import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/home';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <main>
          <div className="pt-20 min-h-[60vh] flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-4xl text-foreground-400"></i>
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground-900">文章未找到</h1>
              <p className="mt-2 text-foreground-500">抱歉，您要找的文章不存在或已被移除。</p>
              <Link
                to="/"
                className="inline-block mt-6 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
              >
                返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '影视资讯' },
    { label: article.title },
  ];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.thumbnail,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '白云视频',
    },
  };

  const contentParagraphs = article.content
    .split('\n\n')
    .filter((p) => p.trim());

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <Navbar />
      <main>
        <article className="pt-20 bg-background-50">
          <div className="w-full px-4 md:px-6 pb-14">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <div className="mt-6">
                <span className="text-xs font-medium text-primary-500 bg-primary-50 px-3 py-1 rounded-full">
                  {article.category}
                </span>
              </div>

              <h1 className="mt-4 text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight">
                {article.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span>{article.date}</span>
                <span>{article.readTime}阅读</span>
                <span><i className="ri-eye-line text-xs"></i> {article.views} 阅读</span>
              </div>

              <div className="mt-8 aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  title={`${article.title} - 白云视频影视资讯`}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="mt-8 prose max-w-none">
                {contentParagraphs.map((paragraph, index) => {
                  const isHeading = paragraph.startsWith('## ');
                  if (isHeading) {
                    return (
                      <h2 key={index} className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-8 mb-4">
                        {paragraph.replace('## ', '')}
                      </h2>
                    );
                  }
                  return (
                    <p key={index} className="text-foreground-700 leading-relaxed mb-4 text-base">
                      {paragraph}
                    </p>
                  );
                })}
              </div>

              <div className="mt-10 pt-8 border-t border-background-200">
                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {articles
                    .filter((a) => a.id !== article.id)
                    .slice(0, 2)
                    .map((related) => (
                      <Link
                        key={related.id}
                        to={`/article/${related.id}`}
                        className="group flex gap-4 p-4 rounded-lg bg-background-100 hover:bg-background-200 transition-colors"
                      >
                        <div className="w-24 h-16 shrink-0 rounded overflow-hidden bg-background-300">
                          <img
                            src={related.thumbnail}
                            alt={related.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                            {related.title}
                          </h4>
                          <p className="mt-1 text-xs text-foreground-500">{related.date}</p>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}